// Contenedor ÚNICO de reservación de la plantilla V2.
//
// Un solo modal (escritorio ~780 px, móvil pantalla completa) que no cambia de
// ancho, cabecera, fondo, radio, sombra ni posición del botón de cierre durante
// los tres pasos: 1. Tu selección — 2. Tus datos — 3. Pago.
//
// El widget oficial de Bókun NO participa en este flujo.
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getTourPolicy } from "@/lib/bokun-policy.functions";
import { calendarCopy } from "@/lib/calendar-copy";
import { useServerFn } from "@tanstack/react-start";
import { AlertCircle, ArrowLeft, Check, Loader2, Lock, Pencil, X } from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { startBokunTestBooking } from "@/lib/bokun-test-booking.functions";
import { startBokunLiveBooking } from "@/lib/bokun-live-booking.functions";
import { HoldCheckoutPanel } from "@/components/booking/HoldCheckoutPanel";
import { TOUR_V2_OWN_CHECKOUT } from "@/lib/tour-v2-flag";
import {
  ageLabel,
  rowAmount,
  type TourV2Quote,
  type TourV2Selection,
} from "@/components/tour/v2/tour-v2-selection";
import { clearRecentTour } from "@/lib/recent-tour";
import { trackTourV2 } from "@/lib/tour-v2-analytics";

import { useCurrency, formatMoney, normalizeCurrency } from "@/lib/currency";
import { chargeDisclosure } from "@/lib/booking-display";
import { participantLabel } from "@/lib/participant-labels";
import { useFormatPrice } from "@/hooks/use-format-price";
import { cn } from "@/lib/utils";
import { PaymentBrandNotice } from "@/components/booking/PaymentBrandNotice";
import { BookingPhoneInput } from "@/components/booking/BookingPhoneInput";
import { bookingPhone, defaultPhoneCountry } from "@/lib/booking-phone";
import { missingBookingTransfers } from "@/lib/booking-transfer";
import { BookingPlaceSelect } from "@/components/booking/BookingPlaceSelect";
import { WhatsApp24 } from "@/components/site/WhatsApp24";
import { logisticsCopy, returnTransfer, type BookingPlace } from "@/lib/booking-logistics";

type Step = 1 | 2 | 3;

export interface TourV2BookingFlowProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: Step;
  onStepChange: (step: Step) => void;
  tourTitle: string;
  image: string | null;
  activityId: string | null;
  /** Tarifa de la salida elegida (sandbox). */
  rateId?: string | null;
  currency: string;
  priceFrom: number | null;
  pricingCategories: { id: string; title: string; minAge: number | null; maxAge: number | null }[];
  mandatoryFees?: { id: string; title: string; price: number; currency: string | null }[];
  minBookable: number | null;
  maxBookable: number | null;
  pickupOptions: { id: string; title: string; address: string | null }[];
  dropoffOptions?: BookingPlace[];
  /** Leído del producto en Bókun: recogida obligatoria / punto de regreso. */
  pickupAvailable?: boolean;
  pickupRequired?: boolean;
  dropoffAvailable?: boolean;
  dropoffRequired?: boolean;
  cancellationPolicy: string | null;
  freeCancellationHours?: number | null;
  /** Zona de recogida precargada (enlace MCP). */
  initialPickup?: string | null;
  selection: TourV2Selection;
  onSelectionChange: (next: TourV2Selection) => void;
  quote: TourV2Quote | null;
  onQuoteChange: (q: TourV2Quote) => void;
  /** Maya: permite el cobro de pruebas aunque la bandera global esté apagada. */
  ownCheckout?: boolean;
  /** Entorno de la canalización: prueba (defecto) o producción. */
  env?: "test" | "live";
  /** Aviso interno: el producto no tiene testProductId (pago deshabilitado). */
  notConfiguredMessage?: string | null;
  /** Maya: capa por encima del panel del chat. */
  layerClassName?: string;
  /** Maya: campo de requerimientos especiales y datos conservados en la pestaña. */
  extendedForm?: boolean;
  onBookingEvent?: (ev: "checkout_started" | "payment_redirect" | "failed") => void;
}

const MAYA_CONTACT_KEY = "mm:maya-contact:v1";
const ATTEMPT_KEY = "mm:v2-pay-attempt:v1";

import { CANCELLATION_POLICY_VERSION, effectiveCancellationPolicy, cancellationUi, recoveryConsentLabel } from "@/lib/cancellation-policy";
export function TourV2BookingFlow(props: TourV2BookingFlowProps) {
  useCurrency(); // subscribe: formatPrice reads the currency store
  const { t, i18n } = useTranslation();
  const formatPrice = useFormatPrice();
  const { currency: displayCurrency, rates } = useCurrency();
  const startTest = useServerFn(startBokunTestBooking);
  const startLive = useServerFn(startBokunLiveBooking);
  const startBooking = props.env === "live" ? startLive : startTest;
  const ownCheckout = props.ownCheckout ?? TOUR_V2_OWN_CHECKOUT;
  const policy = useQuery({queryKey:["checkout-policy",props.activityId,props.rateId,props.selection.date,props.selection.startTimeId,i18n.language],queryFn:()=>getTourPolicy({data:{id:props.activityId ?? "",rateId:props.rateId,date:props.selection.date,startTimeId:props.selection.startTimeId == null ? null : String(props.selection.startTimeId),language:i18n.language}}),enabled:props.open && !!props.activityId && props.env === "live",staleTime:0,refetchInterval:60_000});
  const acceptedVersion = props.env === "live" ? policy.data?.version : CANCELLATION_POLICY_VERSION;
  // Maya: los datos se conservan en esta pestaña si algo falla (nunca pago).
  const saved = useMemo(() => {
    if (!props.extendedForm || typeof window === "undefined") return null;
    try {
      return JSON.parse(window.sessionStorage.getItem(MAYA_CONTACT_KEY) || "null");
    } catch {
      return null;
    }
  }, [props.extendedForm]);
  const [contact, setContact] = useState({
    firstName: saved?.firstName ?? "",
    lastName: saved?.lastName ?? "",
    email: saved?.email ?? "",
    phone: saved?.phone ?? "",
    country: saved?.country ?? "",
  });
  const [pickupPlaceId, setPickupPlaceId] = useState("");
  const [dropoffPlaceId, setDropoffPlaceId] = useState("");
  const [sameReturn, setSameReturn] = useState(true);
  const logistics = logisticsCopy(i18n.language);
  const [phoneCountry, setPhoneCountry] = useState(() => defaultPhoneCountry(i18n.language));
  const [phoneCountryChosen, setPhoneCountryChosen] = useState(false);
  useEffect(() => {
    if (!phoneCountryChosen) setPhoneCountry(defaultPhoneCountry(i18n.language));
  }, [i18n.language, phoneCountryChosen]);
  const internationalPhone = bookingPhone(contact.phone, phoneCountry);
  const [pickupNote, setPickupNote] = useState<string>(saved?.pickupNote ?? "");
  const [special, setSpecial] = useState<string>(saved?.special ?? "");
  const [dropoffNote, setDropoffNote] = useState<string>(saved?.dropoffNote ?? "");
  const [touched, setTouched] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  useEffect(()=>{setPolicyAccepted(false);},[acceptedVersion,i18n.language,props.selection.date,props.selection.startTimeId,props.rateId]);
  const [recoveryConsent, setRecoveryConsent] = useState(false);
  const cancelUi = cancellationUi(i18n.language);
  useEffect(() => {
    if (!props.extendedForm) return;
    try {
      window.sessionStorage.setItem(
        MAYA_CONTACT_KEY,
        JSON.stringify({ ...contact, pickupNote, dropoffNote, special }),
      );
    } catch {
      /* noop */
    }
  }, [props.extendedForm, contact, pickupNote, dropoffNote, special]);
  const [error, setError] = useState<string | null>(null);
  // Nuevo total devuelto por el servidor y que el cliente debe confirmar.
  const [acceptedAmount, setAcceptedAmount] = useState<number | null>(null);

  const quote = props.quote;
  const step = props.step;

  const dateLabel = useMemo(() => {
    if (!quote?.selection.date) return null;
    const [y, m, d] = quote.selection.date.split("-").map(Number);
    return new Intl.DateTimeFormat(i18n.language, {
      weekday: "short",
      day: "numeric",
      month: "long",
    }).format(new Date(y, m - 1, d));
  }, [quote?.selection.date, i18n.language]);

  useEffect(() => {
    const z = props.initialPickup?.trim();
    if (!z) return;
    const n = (v: string) => v.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const match = props.pickupOptions.find((o) => n(o.title) === n(z) || n(o.title).includes(n(z)));
    if (match) setPickupPlaceId(match.id);
    else setPickupNote((cur) => cur || z);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialPickup]);
  // Bókun puede exigir recogida/regreso aunque el producto no lo declare.
  const [forced, setForced] = useState<{ pickup: boolean; dropoff: boolean }>({ pickup: false, dropoff: false });
  const pickupRequired = !!props.pickupRequired || forced.pickup;
  const dropoffRequired = !!props.dropoffRequired || forced.dropoff;
  const returnValues = returnTransfer(sameReturn, pickupPlaceId, pickupNote, props.pickupOptions, props.dropoffOptions ?? [], dropoffPlaceId, dropoffNote);
  const missingTransfers = missingBookingTransfers({ pickupRequired, dropoffRequired }, { pickupPlaceId, pickupNote, ...returnValues });
  const pickupMissing = missingTransfers.includes("pickup");
  const dropoffMissing = missingTransfers.includes("dropoff");
  const basicReady =
    !!contact.firstName.trim() &&
    !!contact.lastName.trim() &&
    /.+@.+\..+/.test(contact.email) &&
    !!contact.country.trim();
  const contactReady =
    !!contact.firstName.trim() &&
    !!contact.lastName.trim() &&
    /.+@.+\..+/.test(contact.email) &&
    !!contact.country.trim() &&
    !!internationalPhone &&
    !pickupMissing &&
    !dropoffMissing;
  const [fieldError, setFieldError] = useState<"pickup" | "dropoff" | "phone" | null>(null);

  // Bloqueo síncrono: evita que clics rápidos creen varias reservas antes de
  // que isPending se refleje en pantalla.
  const payLock = useRef(false);
  const [hold, setHold] = useState<{
    clientSecret: string;
    amount?: number;
    currency?: string;
    payDeadline: string;
    idempotencyKey: string;
  } | null>(null);
  const pay = useMutation({
    mutationFn: async () => {
      if (!quote || !props.activityId) throw new Error("");
      if (missingTransfers.length) {
        setTouched(true);
        setFieldError(missingTransfers[0] ?? null);
        props.onStepChange(1);
        throw new Error("MISSING_TRANSFER");
      }
      if (!internationalPhone) {
        setFieldError("phone");
        props.onStepChange(1);
        throw new Error("INVALID_PHONE");
      }
      props.onBookingEvent?.("checkout_started");
      // Sin cobros ni reservas duplicadas: el mismo intento (misma selección y
      // correo) reutiliza el pago ya abierto durante 25 min en lugar de crear
      // otra reserva temporal en Bókun.
      const attemptKey = JSON.stringify([
        props.activityId,
        quote.selection.date,
        quote.selection.startTimeId,
        quote.selection.qty,
        contact.email.trim().toLowerCase(),
        pickupPlaceId,
        pickupNote.trim(),
        returnValues.dropoffPlaceId,
        returnValues.dropoffNote.trim(),
        props.rateId,
      ]);
      // Misma selección = misma clave de idempotencia: el servidor devuelve el
      // mismo pago en lugar de apartar otro lugar o crear otro cobro.
      let idempotencyKey: string | null = null;
      try {
        const prev = JSON.parse(window.sessionStorage.getItem(ATTEMPT_KEY) || "null");
        if (prev?.key === attemptKey && Date.now() - prev.at < 25 * 60_000 && prev.idem) {
          idempotencyKey = String(prev.idem);
        }
      } catch {
        /* noop */
      }
      if (!idempotencyKey) {
        idempotencyKey = `v2_${crypto.randomUUID().replace(/-/g, "")}`;
        try {
          window.sessionStorage.setItem(
            ATTEMPT_KEY,
            JSON.stringify({ key: attemptKey, idem: idempotencyKey, at: Date.now() }),
          );
        } catch {
          /* noop */
        }
      }
      const res = await startBooking({
        data: {
          activityId: props.activityId,
          date: quote.selection.date as string,
          startTimeId:
            quote.selection.startTimeId != null ? String(quote.selection.startTimeId) : null,
          rateId: props.rateId ?? null,
          items: quote.rows.map((r) => ({
            pricingCategoryId: r.id,
            quantity: quote.selection.qty[r.id] ?? 0,
          })),
          contact: {
            firstName: contact.firstName.trim(),
            lastName: contact.lastName.trim(),
            email: contact.email.trim(),
            phone: internationalPhone,
            language: (i18n.language || "es").slice(0, 2),
          },
          passengerAnswers: [],
          extras: [],
          pickupPlaceId: pickupPlaceId || null,
          pickupNote: pickupNote.trim().slice(0, 300) || null,
          dropoffPlaceId: returnValues.dropoffPlaceId || null,
          dropoffNote: returnValues.dropoffNote.trim().slice(0, 300) || null,
          idempotencyKey,
          expectedAmount: acceptedAmount ?? quote.total,
          cancellationPolicyVersion: acceptedVersion,
          recoveryConsent,
          resumePath: window.location.pathname,
          answers: [],
        },
      });
      return { ...res, idempotencyKey };
    },
    onSuccess: (res) => {
      if (res.ok) {
        trackTourV2("booking_completed", {
          amount: quote?.total,
          currency: quote?.currency,
          pax: quote?.pax,
        });
        // Compra en marcha: el tour deja de estar en el seguimiento de interés.
        clearRecentTour();
        props.onBookingEvent?.("payment_redirect");
        if (res.clientSecret && res.payDeadline) {
          setHold({
            clientSecret: res.clientSecret,
            amount: typeof res.amount === "number" ? res.amount : undefined,
            currency: typeof res.currency === "string" ? res.currency : undefined,
            payDeadline: res.payDeadline,
            idempotencyKey: res.idempotencyKey,
          });
        } else {
          window.location.assign(res.url);
        }
      } else {
        if (res.code !== "IN_PROGRESS") payLock.current = false;
        props.onBookingEvent?.("failed");
        if ("requires" in res && res.requires?.length) {
          const requires = res.requires;
          setForced((f) => ({
            pickup: f.pickup || requires.includes("pickup"),
            dropoff: f.dropoff || requires.includes("dropoff"),
          }));
        }
        if ("field" in res && res.field) {
          setFieldError(res.field);
          setTouched(true);
          props.onStepChange(1);
        }
        if (res.code === "PRICE_CHANGED" && typeof res.newAmount === "number") {
          // El cliente confirma el nuevo total con el siguiente clic.
          setAcceptedAmount(res.newAmount);
          setError(
            `Actualizamos el precio, revisa el total: ${new Intl.NumberFormat(i18n.language, {
              style: "currency",
              currency: res.currency || quote?.currency || "MXN",
            }).format(res.newAmount)}`,
          );
          return;
        }
        setError(res.error);
      }
    },
    onError: () => {
      props.onBookingEvent?.("failed");
      setError(
        t("tourV2.payError", {
          defaultValue: "No pudimos apartar tu lugar. Vuelve a intentarlo en unos minutos.",
        }) as string,
      );
    },
  });

  // La selección ya ocurrió en el hero: el contenedor empieza en "Tus datos".
  const steps: { n: Step; label: string }[] = [
    { n: 1, label: t("tourV2.stepData", { defaultValue: "Tus datos" }) as string },
    { n: 2, label: t("tourV2.stepPay", { defaultValue: "Pago" }) as string },
    { n: 3, label: t("tourV2.stepConfirm", { defaultValue: "Confirmación" }) as string },
  ];

  const summary = quote && quote.pax > 0 && (
    <div className="flex gap-3 rounded-2xl border border-border/70 bg-muted/40 p-3">
      {props.image && (
        <img
          src={props.image}
          alt={props.tourTitle}
          loading="lazy"
          className="h-16 w-20 shrink-0 rounded-xl object-cover"
        />
      )}
      <div className="min-w-0 flex-1 text-sm">
        <p className="truncate font-semibold">{props.tourTitle}</p>
        <p className="text-xs text-muted-foreground">
          {[dateLabel, quote.selection.startTime].filter(Boolean).join(" · ")}
        </p>
        <p className="text-xs text-muted-foreground">
          {quote.rows
            .filter((r) => (quote.selection.qty[r.id] ?? 0) > 0)
            .map((r) =>
              r.perBooking
                ? `${r.title} · ${quote.selection.qty[r.id]} ${quote.selection.qty[r.id] === 1 ? t("tourV2.person", { defaultValue: "persona" }) : t("tourV2.people", { defaultValue: "personas" })}`
                : participantLabel(quote.selection.qty[r.id], r.title, i18n.language),
            )
            .join(" · ")}
        </p>
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="font-bold">
            {formatPrice(quote.total, quote.currency)}{" "}
            <span className="text-xs font-normal text-muted-foreground">{displayCurrency}</span>
          </span>
          {step < 3 && (
            <button
              type="button"
              // Regresa al calendario propio del hero conservando la selección.
              onClick={() => props.onOpenChange(false)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            >
              <Pencil className="h-3.5 w-3.5" />
              {t("tourV2.editSelection", { defaultValue: "Editar selección" })}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Precarga Stripe.js y el panel de pago desde que se abre la reserva propia:
  // al tener el apartado listo, el formulario de tarjeta se monta sin esperar descargas.
  useEffect(() => {
    if (!ownCheckout || !props.open) return;
    void import("@/components/booking/StripeEmbeddedPanel").catch(() => null);
    void import("@/lib/stripe-browser").then((m) => (m.isStripeConfigured() ? m.getStripe() : null)).catch(() => null);
  }, [ownCheckout, props.open]);

  // Estado visible mientras se aparta el lugar y se prepara el pago.
  const [pendingPhase, setPendingPhase] = useState(0);
  useEffect(() => {
    if (!pay.isPending) { setPendingPhase(0); return; }
    // Solo presentación: el apartado en Bókun tarda ≈2 s; después se prepara el pago.
    const a = window.setTimeout(() => setPendingPhase(1), 2000);
    return () => window.clearTimeout(a);
  }, [pay.isPending]);
  const pendingLabel = [
    t("tourV2.phaseHolding", { defaultValue: "Apartando tu lugar…" }),
    t("tourV2.phasePreparing", { defaultValue: "Preparando pago seguro…" }),
  ][pendingPhase];

  return (
    <Dialog
      open={props.open}
      onOpenChange={(next) => {
        if (!next && step < 3 && !pay.isPending) {
          trackTourV2("booking_abandoned", {
            pax: quote?.pax,
            amount: quote?.total,
            currency: quote?.currency,
          });
        }
        props.onOpenChange(next);
      }}
    >
      <DialogContent
        overlayClassName={props.layerClassName}
        className={cn(
          props.layerClassName,
          // Un solo contenedor: mismo ancho, fondo, radio y sombra en los 3 pasos.
          "flex flex-col gap-0 overflow-hidden bg-card p-0 shadow-[0_24px_60px_rgba(23,23,23,0.22)] [&>button]:hidden",
          "top-0 left-0 h-[100dvh] max-h-[100dvh] w-full max-w-none translate-x-0 translate-y-0 rounded-none",
          "sm:top-1/2 sm:left-1/2 sm:h-auto sm:max-h-[88vh] sm:w-[92vw] sm:max-w-[780px] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-3xl",
        )}
      >
        {/* Cabecera fija: idéntica en los tres pasos */}
        <div className="shrink-0 border-b border-border/70 px-4 py-3 sm:px-6">
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="tour-display text-base sm:text-lg">
              {t("tourV2.bookTitle", { defaultValue: "Reserva tu experiencia" })}
            </DialogTitle>
            <button
              type="button"
              onClick={() => props.onOpenChange(false)}
              aria-label={t("common.close", { defaultValue: "Cerrar" }) as string}
              className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-border transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Indicador 1 — 2 — 3 */}
          <ol className="mt-2 flex items-center gap-2 text-xs">
            {steps.map((s, i) => (
              <li key={s.n} className="flex items-center gap-2">
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold",
                    step === s.n
                      ? "bg-primary text-primary-foreground"
                      : step > s.n
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground",
                  )}
                  aria-current={step === s.n ? "step" : undefined}
                >
                  {step > s.n ? <Check className="h-3.5 w-3.5" /> : <span>{s.n}.</span>}
                  {s.label}
                </span>
                {i < steps.length - 1 && (
                  <span aria-hidden className="text-muted-foreground/60">
                    —
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>

        {/* Cuerpo con desplazamiento interno */}
        <div
          className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6"
          style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
        >
          {summary}

          {step === 1 && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="v2-first">
                    {t("tourV2.firstName", { defaultValue: "Nombre" })}
                  </Label>
                  <Input
                    id="v2-first"
                    autoComplete="given-name"
                    value={contact.firstName}
                    onChange={(e) => setContact((c) => ({ ...c, firstName: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="v2-last">
                    {t("tourV2.lastName", { defaultValue: "Apellidos" })}
                  </Label>
                  <Input
                    id="v2-last"
                    autoComplete="family-name"
                    value={contact.lastName}
                    onChange={(e) => setContact((c) => ({ ...c, lastName: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="v2-email">
                    {t("tourV2.email", { defaultValue: "Correo electrónico" })}
                  </Label>
                  <Input
                    id="v2-email"
                    type="email"
                    autoComplete="email"
                    value={contact.email}
                    onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="v2-phone">
                    {t("tourV2.phone", { defaultValue: "Teléfono" })}
                  </Label>
                  <BookingPhoneInput
                    id="v2-phone"
                    country={phoneCountry}
                    value={contact.phone}
                    invalid={fieldError === "phone"}
                    onCountryChange={(country) => {
                      setPhoneCountry(country);
                      setPhoneCountryChosen(true);
                      setFieldError(null);
                    }}
                    onChange={(value) => {
                      setContact((c) => ({ ...c, phone: value }));
                      if (fieldError === "phone") setFieldError(null);
                    }}
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="v2-country">
                    {t("tourV2.country", { defaultValue: "País" })}
                  </Label>
                  <Input
                    id="v2-country"
                    autoComplete="country-name"
                    value={contact.country}
                    onChange={(e) => setContact((c) => ({ ...c, country: e.target.value }))}
                  />
                </div>
              </div>

              {(props.pickupAvailable || pickupRequired || props.pickupOptions.length > 0) && (
                <BookingPlaceSelect id="v2-pickup" label={`${logistics[6]}${pickupRequired ? " *" : ""}`} places={props.pickupOptions} value={pickupPlaceId} note={pickupNote} lang={i18n.language} invalid={(touched || fieldError === "pickup") && pickupMissing} onChange={(id,note)=>{setPickupPlaceId(id);setPickupNote(note);setFieldError(null);}}/>
              )}
              {(props.dropoffAvailable || dropoffRequired) && (
                <div className="space-y-3">
                  <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1 size-4 accent-primary" checked={sameReturn} onChange={e=>setSameReturn(e.target.checked)}/><span>{logistics[8]}</span></label>
                  {sameReturn ? <p className="text-sm text-muted-foreground">{logistics[7]}: {returnValues.dropoffNote || props.pickupOptions.find(p=>p.id===pickupPlaceId)?.title || logistics[11]}</p> : <BookingPlaceSelect id="v2-dropoff" label={`${logistics[7]}${dropoffRequired ? " *" : ""}`} places={props.dropoffOptions ?? []} value={dropoffPlaceId} note={dropoffNote} lang={i18n.language} invalid={(touched || fieldError === "dropoff") && dropoffMissing} onChange={(id,note)=>{setDropoffPlaceId(id);setDropoffNote(note);setFieldError(null);}}/>}
                  {(touched || fieldError === "dropoff") && dropoffMissing && <p data-testid="v2-dropoff-error" className="text-xs text-destructive">{t("tourV2.dropoffRequiredError", { defaultValue: "Indica dónde te dejamos al terminar el tour." })}</p>}
                </div>
              )}
              {props.extendedForm && (
                <div className="space-y-1.5">
                  <Label htmlFor="v2-special">
                    {t("tourV2.special", { defaultValue: "Requerimientos especiales (opcional)" })}
                  </Label>
                  <Input
                    id="v2-special"
                    value={special}
                    maxLength={250}
                    onChange={(e) => setSpecial(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          {step === 2 && hold && (
            <HoldCheckoutPanel
              clientSecret={hold.clientSecret}
              payDeadline={hold.payDeadline}
              idempotencyKey={hold.idempotencyKey}
              onExpired={() => {
                setHold(null);
                pay.reset();
                payLock.current = false;
                try {
                  window.sessionStorage.removeItem(ATTEMPT_KEY);
                } catch {
                  /* noop */
                }
                setError(
                  t("tourV2.holdExpired", {
                    defaultValue:
                      "Se terminó el tiempo para pagar y liberamos tu lugar. No se hizo ningún cargo. Toca «Pagar» para apartarlo de nuevo.",
                  }),
                );
              }}
            />
          )}
          {step === 2 && quote && !hold && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-border/70 p-3 text-sm">
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {t("tourV2.breakdown", { defaultValue: "Desglose del precio" })}
                </p>
                <ul className="mt-2 space-y-1">
                  {quote.rows
                    .filter((r) => (quote.selection.qty[r.id] ?? 0) > 0)
                    .map((r) => {
                      const ages = ageLabel(r);
                      return (
                        <li key={r.id} className="flex justify-between gap-3">
                          <span>
                            {r.perBooking
                              ? `${r.title} · ${quote.selection.qty[r.id]} ${quote.selection.qty[r.id] === 1 ? t("tourV2.person", { defaultValue: "persona" }) : t("tourV2.people", { defaultValue: "personas" })}`
                              : participantLabel(quote.selection.qty[r.id], r.title, i18n.language)}
                            {ages ? ` (${ages})` : ""}
                          </span>
                          {props.env !== "live" && <span>
                            {r.price > 0
                              ? formatPrice(rowAmount(r, quote.selection.qty[r.id] ?? 0), r.currency)
                              : t("tourV2.free", { defaultValue: "Gratis" })}
                          </span>}
                        </li>
                      );
                    })}
                  {(props.env === "live" ? [] : props.mandatoryFees ?? []).map((f) => (
                    <li key={f.id} className="flex justify-between gap-3 text-muted-foreground">
                      <span>{f.title}</span>
                      <span>{formatPrice(f.price, f.currency ?? quote.currency)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 flex items-center justify-between border-t border-border/70 pt-2 font-bold">
                  <span>{t("tourV2.total", { defaultValue: "Total" })}</span>
                  <span>
                    {formatPrice(quote.total, quote.currency)}{" "}
                    <span className="text-xs font-normal text-muted-foreground">
                      {displayCurrency}
                    </span>
                  </span>
                </div>
              </div>

              {props.env !== "live" && props.cancellationPolicy && (
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {props.cancellationPolicy}
                </p>
              )}

              <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                <Lock className="h-3.5 w-3.5" aria-hidden />
                {t("tourV2.securePayment", { defaultValue: "Pago seguro cifrado con Stripe" })}
              </p>

              {props.notConfiguredMessage && (
                <div
                  data-testid="v2-not-configured"
                  className="flex items-start gap-2 rounded-2xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <p>{props.notConfiguredMessage}</p>
                </div>
              )}
              {!ownCheckout && !props.notConfiguredMessage && (
                <div className="flex items-start gap-2 rounded-2xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <p>
                    {t("tourV2.checkoutDisabled", {
                      defaultValue:
                        "El cobro propio está activo sólo en el entorno de pruebas. Actívalo para completar el pago desde esta pantalla.",
                    })}
                  </p>
                </div>
              )}

              {error && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-2xl border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <p>{error}</p>
                </div>
              )}
              {error && acceptedAmount == null && <WhatsApp24 />}
            </div>
          )}

          {step === 3 && (
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t("tourV2.confirmPending", {
                defaultValue: "Estamos verificando tu reserva.",
              })}
            </p>
          )}
        </div>

        {/* Pie fijo: único botón principal, siempre en el mismo lugar */}
        <div
          className="shrink-0 border-t border-border/70 bg-card px-4 py-3 sm:px-6"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
        >
          {step === 2 && !hold && (
            <div className="mb-3 rounded-xl border border-border bg-muted/40 p-3 text-xs">
              <p className="font-semibold">{cancelUi.title}</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-muted-foreground">
                {(props.env === "live" ? policy.data?.lines ?? [policy.isError ? calendarCopy(i18n.language).error : calendarCopy(i18n.language).loading] : effectiveCancellationPolicy(i18n.language, props.freeCancellationHours)).map((l) => <li key={l}>{l}</li>)}
              </ul>
              <label className="mt-2 flex items-center gap-2 font-semibold">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[hsl(var(--primary))]"
                  checked={policyAccepted}
                  onChange={(e) => setPolicyAccepted(e.target.checked)}
                />
                {cancelUi.accept}
              </label>
              <label className="mt-1.5 flex items-center gap-2 text-muted-foreground">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={recoveryConsent}
                  onChange={(e) => setRecoveryConsent(e.target.checked)}
                />
                {recoveryConsentLabel(i18n.language)}
              </label>
            </div>
          )}
          {step === 2 && quote && <p className="mb-3 text-sm font-semibold" role="status">{chargeDisclosure(acceptedAmount ?? quote.total, quote.currency, displayCurrency, i18n.language, rates)}</p>}
          {step === 2 && !hold && <PaymentBrandNotice className="mb-3" />}
          {step === 2 && pay.isPending && (
            <div className="mb-3" role="status" aria-live="polite">
              <div className="mb-1 flex justify-between text-xs font-semibold text-muted-foreground">
                <span>{pendingLabel}</span>
                <span>{pendingPhase + 1}/2</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-700 motion-reduce:transition-none"
                  style={{ width: pendingPhase === 0 ? "45%" : "85%" }}
                />
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            {step === 2 && (
              <button
                type="button"
                onClick={() => {
                  if (hold) return;
                  props.onStepChange(1);
                }}
                disabled={!!hold}
                className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full border border-border px-4 text-sm font-semibold transition hover:bg-accent"
              >
                <ArrowLeft className="h-4 w-4" />
                {t("common.back", { defaultValue: "Atrás" })}
              </button>
            )}
            {step === 2 && hold ? (
              // Con el formulario de Stripe visible, el único botón de pago es el suyo.
              <p className="flex-1 text-right text-sm font-semibold" role="status">
                {t("tourV2.willCharge", { defaultValue: "Se cobrará" })}{" "}
                {formatMoney(hold.amount ?? acceptedAmount ?? quote?.total ?? 0, normalizeCurrency(hold.currency ?? quote?.currency ?? "MXN"), { locale: i18n.language, decimals: 2 })}{" "}
                {hold.currency ?? quote?.currency}
              </p>
            ) : (
            <button
              type="button"
              disabled={
                (step === 1 && !basicReady) ||
                (step === 2 && (!ownCheckout || !policyAccepted || (props.env === "live" && (!policy.data || policy.isError || policy.isFetching)) || pay.isPending || (pay.isSuccess && !!pay.data?.ok) || !!hold)) || step === 3
              }
              onClick={() => {
                setError(null);
                if (step === 1) {
                  // El error aparece junto al campo antes de llegar al pago.
                  setTouched(true);
                  if (!internationalPhone) {
                    setFieldError("phone");
                    document.getElementById("v2-phone")?.focus();
                    return;
                  }
                  if (missingTransfers.length) {
                    const field = missingTransfers[0];
                    setFieldError(field ?? null);
                    document.getElementById(field === "pickup" ? "v2-hotel" : "v2-dropoff")?.focus();
                    return;
                  }
                  setFieldError(null);
                  if (contactReady) props.onStepChange(2);
                }
                else if (step === 2) {
                  if (payLock.current) return;
                  payLock.current = true;
                  pay.mutate(undefined, {
                    onError: () => {
                      payLock.current = false;
                    },
                  });
                }
              }}
              className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-full bg-primary px-5 text-sm font-bold text-primary-foreground transition hover:brightness-105 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {(pay.isPending || (pay.isSuccess && !!pay.data?.ok)) && <Loader2 className="h-4 w-4 animate-spin" />}
              {step === 2 && (pay.isPending || (pay.isSuccess && pay.data?.ok))
                ? <span aria-live="polite">{pay.isPending ? pendingLabel : t("tourV2.processing", { defaultValue: "Procesando pago…" })}</span>
                : step === 1
                ? t("tourV2.goToPayment", { defaultValue: "Continuar al pago" })
                : acceptedAmount != null && quote
                  ? `${t("tourV2.continueWith", { defaultValue: "Continuar con" })} ${quote.currency} ${formatMoney(acceptedAmount, normalizeCurrency(quote.currency), { locale: i18n.language, decimals: 2 })}`
                  : t("tourV2.continueSecure", { defaultValue: "Continuar al pago seguro" })}
            </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
